import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { searchMembers } from '../../../controllers/memberManagementController';
import { createMemberDues, fetchCurrentDuesPolicy } from '../../../controllers/paymentsController';
import { buildDuesFormDefaults, toApiDateTime } from '../duesFormUtils';
import { formatPaymentOperationError } from '../../../utils/logoPrerequisiteErrors';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '5px',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
  },
  '& .MuiInputBase-input': { fontSize: '14px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
};

const CreateMemberDuesDialog = ({
  open,
  onClose,
  onCreated,
  prefillMemberId = null,
  prefillMemberName = null,
}) => {
  const navigate = useNavigate();
  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [form, setForm] = useState({
    period: '',
    amount: '',
    dueDate: '',
    finalDueDate: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setSubmitting(false);
    (async () => {
      try {
        const policy = await fetchCurrentDuesPolicy();
        setForm(buildDuesFormDefaults(policy));
      } catch {
        setForm(buildDuesFormDefaults(null));
      }
    })();
    if (prefillMemberId) {
      setSelectedMember({
        id: prefillMemberId,
        fullName: prefillMemberName || prefillMemberId,
      });
    } else {
      setSelectedMember(null);
      setMemberQuery('');
      setMemberOptions([]);
    }
  }, [open, prefillMemberId, prefillMemberName]);

  useEffect(() => {
    if (!open || prefillMemberId) return;
    if (!memberQuery || memberQuery.trim().length < 2) {
      setMemberOptions([]);
      return;
    }
    let active = true;
    setMemberLoading(true);
    searchMembers(memberQuery.trim())
      .then((data) => {
        if (!active) return;
        setMemberOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!active) return;
        setMemberOptions([]);
      })
      .finally(() => {
        if (active) setMemberLoading(false);
      });
    return () => { active = false; };
  }, [memberQuery, open, prefillMemberId]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    const memberId = selectedMember?.id;
    if (!memberId) {
      setError('Üye seçiniz');
      return;
    }
    if (!form.period?.trim()) {
      setError('Dönem zorunludur');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      setError('Geçerli bir tutar giriniz');
      return;
    }
    if (!form.dueDate) {
      setError('Vade tarihi zorunludur');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const created = await createMemberDues({
        memberId,
        period: form.period.trim(),
        amount: Number(form.amount),
        dueDate: toApiDateTime(form.dueDate),
        finalDueDate: form.finalDueDate ? toApiDateTime(form.finalDueDate) : null,
        description: form.description?.trim() || null,
        duesPolicyId: null,
      });
      toast.success('Aidat oluşturuldu');
      onClose();
      if (onCreated) onCreated(created);
      if (created?.id) navigate(`/payments/dues/${created.id}`);
    } catch (e) {
      setError(formatPaymentOperationError(e, { actionLabel: 'Aidat oluşturma' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 500, display: 'flex', justifyContent: 'space-between', pr: 3 }}>
        Tek Üyeye Aidat Kes
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
          <Alert severity="info" sx={{ fontSize: '13px' }}>
            Tutar ve vade formdan gönderilir. Politika yalnızca varsayılan öneri sağlar.
          </Alert>
          {prefillMemberId ? (
            <Box>
              <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Üye</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{prefillMemberName || prefillMemberId}</Typography>
            </Box>
          ) : (
            <Autocomplete
              options={memberOptions}
              loading={memberLoading}
              value={selectedMember}
              onChange={(_, value) => setSelectedMember(value)}
              onInputChange={(_, value) => setMemberQuery(value)}
              getOptionLabel={(o) => o?.fullName || o?.email || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => (
                <TextField {...params} label="Üye" placeholder="Ad ile ara..." sx={fieldSx} />
              )}
            />
          )}
          <TextField label="Dönem" value={form.period} onChange={handleChange('period')} placeholder="2026" fullWidth sx={fieldSx} />
          <TextField label="Tutar" type="number" inputProps={{ step: '0.01', min: 0.01 }} value={form.amount} onChange={handleChange('amount')} fullWidth sx={fieldSx} />
          <TextField label="Vade Tarihi" type="date" value={form.dueDate} onChange={handleChange('dueDate')} InputLabelProps={{ shrink: true }} fullWidth sx={fieldSx} />
          <TextField label="Son Ödeme Tarihi" type="date" value={form.finalDueDate} onChange={handleChange('finalDueDate')} InputLabelProps={{ shrink: true }} fullWidth sx={fieldSx} />
          <TextField label="Açıklama" value={form.description} onChange={handleChange('description')} multiline rows={2} fullWidth sx={fieldSx} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>İptal</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ textTransform: 'none', backgroundColor: '#0E4D94' }}>
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMemberDuesDialog;
